import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { FormGroup } from '@angular/forms';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  CtModelConfiguration,
  CtModelOperator,
  CtModelRouteData,
  CtModelService,
  CtModelType,
  SnackbarService
} from "@ctsolution/ct-framework";
import { CtWebapiGenericResponse } from "@ctsolution/ct-webapi";
import { ContactRouteData } from "../../../_core/route-data/contact.route-data";
import { CompanyContactConfiguration, CompanyContactFilter } from "../../company-contact/company-contact.configuration";
import { ContactFeeManagerConfiguration } from './contact-fee/contact-fee.configuration';
import { CtManagerService } from '../../../_core/lib/ct-manager.service';
import { ContactEditFormComponent } from './contact-edit-form/contact-edit-form.component';

/**
 * Component principale per la gestione dei Contact
 * 
 * Responsabilità:
 * - Coordina tutti i componenti figli (form, information, places, company-contact, fee)
 * - Gestisce il lifecycle e il setup della configurazione
 * - Implementa il submit sincronizzato tra tutte le sezioni
 * - Gestisce la navigazione post-salvataggio
 * 
 * Architettura:
 * - ContactEditFormComponent: Form principale con campi base
 * - ContactInformationComponent: Gestione informazioni contatto (email, telefono, ecc.)
 * - ContactPlaceListComponent: Gestione luoghi/sedi del contact
 * - CompanyContactComponent: Gestione relazioni con le company
 * - ContactFeeComponent: Gestione tariffe (se abilitato)
 * 
 * Pattern utilizzato:
 * - Master-detail con sezioni visibili solo in edit mode
 * - Submit sincronizzato con forkJoin per atomicità
 * - ViewChild per accesso ai componenti figli
 */
@Component({
  selector: 'app-contact-edit',
  templateUrl: './contact-edit.component.html'
})
export class ContactEditComponent {

  /** Riferimento al componente form principale */
  @ViewChild(ContactEditFormComponent) contactEditFormComponent!: ContactEditFormComponent;
  
  /** Riferimento al componente informazioni contatto */
  @ViewChild('contactInformationComponent') contactInformationComponent: any;
  
  /** Riferimento al componente lista places */
  @ViewChild('contactPlaceListComponent') contactPlaceListComponent: any;

  /** Route data per il contact */
  contactModelRouteData: CtModelRouteData = ContactRouteData()
    .setModelType(CtModelType.FORM);

  /** Configurazione principale del contact */
  configuration: CtModelConfiguration<ContactEditComponent> | null = null;
  
  /** Configurazione per le relazioni con le company */
  companyContactConfiguration: CompanyContactConfiguration | null = null;
  
  /** Configurazione per le tariffe (se abilitato) */
  contactFeeConfiguration: ContactFeeManagerConfiguration = ContactFeeManagerConfiguration.create();

  /**
   * Determina se mostrare le sezioni master-detail
   * Visibili solo in modalità edit (quando esiste un id nel route)
   */
  get enableMasterDetail(): boolean {
    return !!this.configuration?.RouteData?.id;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private managerService: CtManagerService,
    private modelService: CtModelService<any>,
    private snackbarService: SnackbarService
  ) {

    this.route.data.subscribe(data => data["title"] =  "CT_MENU.contacts");

  }

  ngAfterViewInit() {
    this.setup();
  }

  /**
   * Setup delle configurazioni del componente
   * Inizializza tutte le sezioni e carica i dati se in modalità edit
   */
  async setup() {

    const pId: string | null = this.route.snapshot.paramMap.get("id");

    // Applica override personalizzati per le tariffe
    this.managerService.getComponentOverride(
      "contact",
      "contactFeeManagerConfiguration",
      this.contactFeeConfiguration
    ).then(() => {
      return;
    });

    // IMPORTANTE: Crea la configurazione principale PRIMA di tutto
    this.configuration = CtModelConfiguration
      .create<ContactEditComponent>()
      .setRouteData(this.contactModelRouteData);

    // Se siamo in modalità edit, setup delle sezioni master-detail
    if (pId) {

      this.contactModelRouteData.setId(+pId);

      this.contactFeeConfiguration.setContactOid(+pId);

      // Carica i dati del contact esistente
      // IMPORTANTE: Aspetta che il form sia inizializzato prima di caricare i dati
      setTimeout(() => {
        this.modelService
          .getInfos(this.configuration!)
          .subscribe({
            next: async (response) => {

              const dataSource = response?.Result?.DataSource;

              if (dataSource && this.contactEditFormComponent) {
                
                // IMPORTANTE: Trasforma il campo Type da oggetto {Oid: value} a numero
                // Il form si aspetta "Type": 3 invece di "Type": {"Oid": 3}
                if (dataSource.Type && typeof dataSource.Type === 'object' && dataSource.Type.Oid !== undefined) {
                  dataSource.Type = dataSource.Type.Oid;
                }
                
                // Popola il form con i dati esistenti
                this.contactEditFormComponent.setup(dataSource);
              }

            },
            error: (err) => console.error('Errore durante il recupero delle informazioni:', err)
          });
      }, 500);

      this.setupCompanyContactConfiguration();

    }

  }

  /**
   * Setup della configurazione per le relazioni con le company
   */
  setupCompanyContactConfiguration() {

    this.companyContactConfiguration = CompanyContactConfiguration
      .create()
      .setOperations([CtModelOperator.Update])
      .setCompanyContactFilter(
        CompanyContactFilter
          .create()
          .setField('Contacts.Oid')
          .setValue(this.contactModelRouteData.id))
      .setOnEdit(async contactRelation => {

        const oid = contactRelation.Companies.Oid;

        this.router
          .navigate(['/', 'customer', contactRelation.IsSupplier ? 'supplier' : 'private', 'edit', oid])
          .then();

      })
      .setFieldsCustomTemplates([
        { prop: 'Contacts', visible: false },
        { prop: 'ContactsPriorityInformation', visible: false },
        { prop: 'ContactsType', visible: false },
        { prop: 'Type', visible: false },
      ]);

  }

  /**
   * Submit sincronizzato di tutte le sezioni (contact-edit-form, contact-information, contact-place-list)
   * Questo metodo viene chiamato da qualsiasi pulsante "Salva" nelle sezioni figlie
   * 
   * Flusso:
   * 1. Valida tutti i form (marca come touched per mostrare errori)
   * 2. Se tutti validi, esegue i submit in parallelo con forkJoin
   * 3. Mostra notifica di successo/errore
   * 4. Refresh della pagina
   */
  async submitAll() {

    // 1. Ottieni il pId per verificare se siamo in modalità edit
    const pId: string | null = this.route.snapshot.paramMap.get("id");
    
    // Questo metodo funziona solo in modalità edit (quando abbiamo l'Oid del contact)
    if (!pId) {
      console.error('submitAll() può essere utilizzato solo in modalità edit');
      return;
    }

    // 2. Valida tutti i form e marca come touched
    const allFormsValid = this.validateAllForms();

    // 3. Se almeno un form non è valido, stoppo e mostro gli errori
    if (!allFormsValid) {
      this.snackbarService.open('Alcuni campi non sono validi. Controlla i form.');
      return;
    }

    // 4. Preparo il form principale
    const formGroup = this.contactEditFormComponent?.configuration?.form;
    if (!formGroup) {
      console.error('Form principale non disponibile');
      return;
    }

    const value = formGroup.getRawValue();
    debugger

    // IMPORTANTE: Trasforma il campo Type da numero a oggetto {Oid: value}
    // Il backend si aspetta "Type": {"Oid": 3} invece di "Type": 3
    if (value.Type && typeof value.Type === 'number') {
      value.Type = { Oid: value.Type };
    }
    if (value.InternalReference) {

      value.InternalReference = { Oid: value.InternalReference.value };

    }
    const cleanedValue = this.cleanPayloadForSubmit(value);
   
    // 5. Eseguo i submit di tutte le sezioni in parallelo
    const submitObservables = {
      contactForm: this.submitContactForm(cleanedValue),
      contactInformation: this.submitContactInformation(),
      contactPlaces: this.submitContactPlaces()
    };

    // 6. ForkJoin attende che tutti i submit siano completati
    forkJoin(submitObservables)
      .pipe(
        catchError(error => {
          console.error('Errore durante il submit sincronizzato:', error);
          this.snackbarService.open('Errore durante il salvataggio. Riprova.');
          return of(null);
        })
      )
      .subscribe(results => {
        
        if (results) {
          // 7. Tutti i submit sono andati a buon fine
          this.snackbarService.open('Tutte le modifiche sono state salvate con successo');
          
          // 8. Refresh della pagina
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      });
  }

  /**
   * Valida tutti i form e li marca come touched per mostrare gli errori
   * Utilizza un approccio "safe" per evitare errori su form non completamente inizializzati
   * @returns true se tutti i form sono validi, false altrimenti
   */
  private validateAllForms(): boolean {
    let allValid = true;

    // Valida contact-edit-form (sempre presente)
    try {
      const contactForm = this.contactEditFormComponent?.configuration?.form;
      if (contactForm) {
        this.safeMarkAsTouched(contactForm);
        if (!contactForm.valid) {
          allValid = false;
          console.warn('Contact form non valido');
        }
      }
    } catch (error) {
      console.error('Errore durante la validazione del contact form:', error);
      allValid = false;
    }

    // Valida contact-information (solo se presente e ha un form)
    try {
      if (this.contactInformationComponent?.modelConfiguration?.form) {
        const infoForm = this.contactInformationComponent.modelConfiguration.form;
        this.safeMarkAsTouched(infoForm);
        if (!infoForm.valid) {
          allValid = false;
          console.warn('Contact information form non valido');
        }
      }
    } catch (error) {
      console.error('Errore durante la validazione del contact information form:', error);
      console.warn('Contact information form skippato per errore di inizializzazione');
    }

    // Valida contact-place-list (tutti i form dei places)
    try {
      if (this.contactPlaceListComponent?.configurations) {
        this.contactPlaceListComponent.configurations.forEach((config: CtModelConfiguration<any>, index: number) => {
          try {
            if (config.form) {
              this.safeMarkAsTouched(config.form);
              if (!config.form.valid) {
                allValid = false;
                console.warn(`Contact place ${index + 1} non valido`);
              }
            }
          } catch (placeError) {
            console.error(`Errore durante la validazione del place ${index + 1}:`, placeError);
          }
        });
      }
    } catch (error) {
      console.error('Errore durante la validazione dei contact places:', error);
      console.warn('Contact places skippati per errore di inizializzazione');
    }

    return allValid;
  }

  /**
   * Marca un form come touched in modo sicuro, evitando errori su controlli non inizializzati
   * @param form Il FormGroup da marcare come touched
   */
  private safeMarkAsTouched(form: FormGroup): void {
    try {
      Object.keys(form.controls).forEach(key => {
        try {
          const control = form.get(key);
          if (control) {
            control.markAsTouched();
            if (control instanceof FormGroup) {
              this.safeMarkAsTouched(control);
            }
          }
        } catch (controlError) {
          console.warn(`Impossibile marcare il controllo '${key}' come touched:`, controlError);
        }
      });
    } catch (error) {
      console.error('Errore durante safeMarkAsTouched:', error);
      try {
        form.markAllAsTouched();
      } catch (fallbackError) {
        console.error('Anche il fallback markAllAsTouched è fallito:', fallbackError);
      }
    }
  }

  /**
   * Submit del form principale contact-edit-form
   * @param value I dati del form già processati
   * @returns Observable<any>
   */
  private submitContactForm(value: any): Observable<any> {
    return new Observable(observer => {
      this.modelService
        .putInfos(this.configuration!, value)
        ?.subscribe({
          next: (response: CtWebapiGenericResponse<number | null>) => {
            observer.next(response);
            observer.complete();
          },
          error: (err) => {
            console.error('Errore durante il salvataggio contact form:', err);
            observer.error(err);
          }
        });
    });
  }

  /**
   * Submit del form contact-information
   * @returns Observable<any>
   */
  private submitContactInformation(): Observable<any> {
    // Se non c'è il componente o non ha un form, ritorno un Observable vuoto (successo)
    if (!this.contactInformationComponent?.submitForm) {
      return of({ success: true, message: 'No contact information to save' });
    }

    // Chiamo il metodo submitForm esposto dal componente figlio
    return this.contactInformationComponent.submitForm();
  }

  /**
   * Submit di tutti i contact places
   * @returns Observable<any>
   */
  private submitContactPlaces(): Observable<any> {
    // Se non ci sono places da salvare, ritorno un Observable vuoto (successo)
    if (!this.contactPlaceListComponent?.submitAllPlaces) {
      return of({ success: true, message: 'No contact places to save' });
    }

    // Chiamo il metodo submitAllPlaces esposto dal componente figlio
    return this.contactPlaceListComponent.submitAllPlaces();
  }

  /**
   * Metodo legacy per submit singolo del form principale
   * Manteniamo questo metodo per retrocompatibilità, ma in modalità edit
   * dovrebbe essere usato submitAll() per sincronizzare tutte le sezioni
   */
  submit(formGroup: FormGroup | null) {

    if (!formGroup) return;

    formGroup.markAllAsTouched();

    if (formGroup.valid) {

      const value = formGroup.getRawValue();

      // IMPORTANTE: Trasforma il campo Type da numero a oggetto {Oid: value}
      // Il backend si aspetta "Type": {"Oid": 3} invece di "Type": 3
      if (value.Type && typeof value.Type === 'number') {
        value.Type = { Oid: value.Type };
      }
      if (value.InternalReference) {

      value.InternalReference = { Oid: value.InternalReference.value };

    }

      const cleanedValue = this.cleanPayloadForSubmit(value);

      this.modelService
        .putInfos(this.configuration!, cleanedValue)
        ?.subscribe({
          next: (response: CtWebapiGenericResponse<number | null>) => {

            const commands = ['/', 'contact'];

            if (response.Result && !isNaN(response?.Result)) {

              commands.push('edit', response.Result?.toString());

              this.router.navigate(commands, { queryParamsHandling: 'preserve' });

            } else {

              this.setup();

            }

          },
          error: (err) => console.error('Errore durante l\'operazione:', err)
        });

    }

  }

   /**
   * Pulisce il payload rimuovendo campi null o non necessari
   * In modalità CREATE non dobbiamo inviare: Oid, CreateAt, UpdateAt, LookupInfo, NameSurname
   * @param value Payload da pulire
   * @returns Payload pulito
   */
  private cleanPayloadForSubmit(value: any): any {
    const cleaned: any = {};
    
    // Campi da escludere se null o undefined
    const excludeIfNull = ['Oid', 'CreateAt', 'UpdateAt', 'LookupInfo', 'NameSurname', 'InternalReference'];
    
    Object.keys(value).forEach(key => {
      const val = value[key];
      
      // Se il campo è in excludeIfNull e il valore è null/undefined, skippo
      if (excludeIfNull.includes(key) && (val === null || val === undefined)) {
        return;
      }
      
      // Altrimenti includo il campo
      cleaned[key] = val;
    });
    
    return cleaned;
  }

}
